import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { createElementIdFactory } from "utils/elementIds";

/**
 * Flag definitions in display order.
 * Split into two columns matching the original layout.
 */
const LEFT_FLAGS = [
  { name: "part_interest", label: "Partial Interest Only" },
  { name: "mortgagee_receiver_sale", label: "Mortgagee/Receiver Sale" },
  { name: "off_the_plan", label: "SOLD off the plan" },
  { name: "related_parties", label: "Buyer Vendor Related" },
];

const RIGHT_FLAGS = [
  { name: "leasehold", label: "Leasehold Transfer" },
  { name: "company_transfer", label: "Company Transfer" },
  { name: "extraordinary", label: "Extraordinary" },
  { name: "includes_car_park", label: "Include car park" },
];

const ALL_FLAGS = [...LEFT_FLAGS, ...RIGHT_FLAGS];

function buildInitialFlags(values) {
  const init = {};
  ALL_FLAGS.forEach((f) => {
    init[f.name] = !!values[f.name];
  });
  return init;
}

/**
 * SaleFlagsPanel
 *
 * Owns the 8 boolean sale flags and the sale_notes textarea.
 *
 * part_interest is special: it is also set automatically by SaleCoreFields
 * when interest_transferred changes. The parent passes the updated value via
 * the `partInterestOverride` prop; a useEffect keeps local state in sync.
 *
 * Props:
 *   values            - initial values for all flags + sale_notes
 *   partInterestOverride - updated part_interest value from parent when it
 *                          changes due to interest_transferred editing
 *   onSave(patch)     - called with changed field(s)
 */
function SaleFlagsPanel({ values: initialValues, partInterestOverride, onSave }) {
  const createId = useMemo(() => createElementIdFactory("sale-detail-flags"), []);
  const [flags, setFlags] = useState(() => buildInitialFlags(initialValues));
  const [saleNotes, setSaleNotes] = useState(initialValues.sale_notes ?? "");

  // Sync part_interest when SaleCoreFields pushes an update through the parent
  useEffect(() => {
    if (partInterestOverride !== undefined && partInterestOverride !== null) {
      setFlags((prev) => ({ ...prev, part_interest: !!partInterestOverride }));
    }
  }, [partInterestOverride]);

  function handleFlagChange(event) {
    const { name, checked } = event.target;
    setFlags((prev) => ({ ...prev, [name]: checked }));
    onSave({ [name]: checked });
  }

  function handleNotesBlur() {
    onSave({ sale_notes: saleNotes || null });
  }

  return (
    <Card id={createId("card")}>
      <Card.Header> </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            {LEFT_FLAGS.map((f) => (
              <Form.Group as={Row} controlId={createId(f.name)} key={f.name}>
                <Col>
                  <Form.Check
                    id={createId(f.name)}
                    checked={flags[f.name]}
                    onChange={handleFlagChange}
                    label={f.label}
                    type="checkbox"
                    name={f.name}
                  />
                </Col>
              </Form.Group>
            ))}
          </Col>
          <Col>
            {RIGHT_FLAGS.map((f) => (
              <Form.Group as={Row} controlId={createId(f.name)} key={f.name}>
                <Col>
                  <Form.Check
                    id={createId(f.name)}
                    checked={flags[f.name]}
                    onChange={handleFlagChange}
                    label={f.label}
                    type="checkbox"
                    name={f.name}
                  />
                </Col>
              </Form.Group>
            ))}
          </Col>
        </Row>

        <Form.Group as={Row} controlId={createId("sale-notes")}>
          <span>&nbsp;&nbsp;</span>
          <Form.Label className="text-left">Sale Notes</Form.Label>
          <Form.Control
            id={createId("sale-notes")}
            onChange={(e) => setSaleNotes(e.target.value)}
            value={saleNotes}
            as="textarea"
            rows="4"
            onBlur={handleNotesBlur}
          />
        </Form.Group>
      </Card.Body>
    </Card>
  );
}

export default SaleFlagsPanel;
